import { Axis, AxisType, Crosshair, Plotband, Plotline, Tooltip, XYContainer } from '@unovis/ts'
import { PlotlineLabelPosition } from '@unovis/ts/components/plotline/types'
import { defineComponent, PropType } from 'vue'
import { ChartDataSet, XYRecord } from '~/utils/charts/unovis'
import {
  formatTooltipTitle,
  lineGradientId,
  renderTooltipHtml,
  seriesByType
} from '~/utils/charts/zchart'
import { shortenLargeNumber } from '~/utils/string'
import { DEFAULT_AXIS_OPTIONS, Marker, Region } from './constants'

export default defineComponent({
  props: {
    dataSets: {
      type: Array as PropType<Array<ChartDataSet>>,
      required: true
    },
    labels: {
      type: Array as PropType<Array<string>>,
      default: () => []
    },
    height: {
      type: Number,
      default: 300
    },
    yMarkers: {
      type: Array as PropType<Array<Marker> | null>,
      default: () => null
    },
    yRegions: {
      type: Array as PropType<Array<Region> | null>,
      default: () => null
    },
    palette: {
      type: Array as PropType<Array<string>>,
      default: () => []
    },
    axisOptions: {
      type: Object,
      default: () => DEFAULT_AXIS_OPTIONS
    },
    tooltipOptions: {
      default: null,
      type: Object
    },
    humanizedTooltips: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({})
    },
    xTickFormat: {
      type: String,
      default: 'll'
    },
    yDomain: {
      type: Array as unknown as PropType<[number | undefined, number | undefined] | null>,
      default: null
    },
    xyRecords: {
      type: Array as PropType<Array<XYRecord>>,
      default: () => []
    }
  },
  data() {
    return {
      wrapperName: 'chart-wrapper',
      chart: null as unknown as XYContainer<XYRecord> | null,
      tooltip: null as Tooltip | null,
      chartKey: '',
      updateTimer: null as ReturnType<typeof setTimeout> | null,
      chartId: Math.random().toString(36).slice(2, 8)
    }
  },
  watch: {
    chartSignature() {
      this.recreateChart()
    },
    dataSets: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    labels() {
      this.updateChart()
    },
    yMarkers: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    yRegions: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    palette() {
      this.updateChart()
    }
  },
  computed: {
    wrapperEl(): HTMLElement | null {
      return (this.$refs[this.wrapperName] as HTMLElement) || null
    },
    wrapperStyle(): Record<string, string> {
      return {
        minHeight: `${this.height}px`
      }
    },
    baseSignatureData(): Record<string, unknown> {
      return {
        palette: this.palette,
        labels: this.labels,
        dataSets: this.dataSets.map((dataset) => ({
          name: dataset.name,
          chartType: dataset.chartType
        })),
        height: this.height,
        axisOptions: this.axisOptions,
        yDomain: this.yDomain
      }
    },
    chartSignature(): string {
      return JSON.stringify(this.baseSignatureData)
    }
  },
  methods: {
    initChart() {
      if (!process.client) return
      const wrapper = this.wrapperEl
      if (!wrapper) return
      this.chartKey = this.chartSignature
      this.chart = this.createXYContainer(wrapper)
    },
    recreateChart() {
      if (this.chartKey === this.chartSignature) {
        return
      }
      this.chartKey = this.chartSignature
      this.destroyChart()
      this.initChart()
    },
    updateChart() {
      if (!process.client) return
      if (this.updateTimer) {
        clearTimeout(this.updateTimer)
        this.updateTimer = null
      }
      this.updateTimer = setTimeout(() => {
        if (!this.chart) {
          this.initChart()
          return
        }

        const data = this.xyRecords
        this.chart.setData(data)
        this.chart.render()
        this.afterChartUpdate()
      }, 100)
    },
    destroyChart() {
      this.tooltip?.destroy?.()
      this.tooltip = null
      this.chart?.destroy?.()
      this.chart = null
    },
    createXYContainer(wrapper: HTMLElement) {
      const x = (d: XYRecord) => d.x
      const yDomain = this.yDomain
      const axisOptions = Object.assign({}, DEFAULT_AXIS_OPTIONS, this.axisOptions)

      const xTickConfig: Record<string, unknown> = {}
      if (axisOptions.xTickValues) {
        xTickConfig.tickValues = axisOptions.xTickValues
      } else if (axisOptions.xNumTicks != null) {
        xTickConfig.numTicks = axisOptions.xNumTicks
      }

      const xAxis = new Axis({
        type: AxisType.X,
        gridLine: false,
        domainLine: true,
        tickTextColor: '#60646D',
        ...xTickConfig,
        tickFormat: (value: number | Date) => {
          const index = typeof value === 'number' ? value : 0
          const label = this.labels[index] ?? ''
          return formatTooltipTitle(label, this.xTickFormat)
        }
      })

      const yAxis = new Axis({
        type: AxisType.Y,
        gridLine: axisOptions.yGridLine !== false,
        domainLine: false,
        tickTextColor: '#60646D',
        tickFormat: (value: number | Date) => {
          if (axisOptions.yTickLabels === false) return ''
          if (value instanceof Date) {
            return value.toISOString()
          }
          return axisOptions.shortenYAxisNumbers ? String(shortenLargeNumber(value)) : String(value)
        }
      })

      const components = [
        ...this.buildChartComponents(x),
        ...this.createPlotlineComponents(),
        ...this.createPlotbandComponents()
      ]

      const tooltip = this.createTooltip()
      this.tooltip = tooltip
      const crosshair = this.createCrosshair(tooltip)

      const containerConfig: Record<string, unknown> = {
        components,
        xAxis,
        yAxis,
        height: this.height,
        margin: axisOptions.margin ?? { top: 20, right: 20, bottom: 20, left: 20 },
        padding: axisOptions.padding ?? { left: 25, right: 25 },
        clipPathExtend: 20,
        ...(yDomain ? { yDomain } : {}),
        ...(tooltip ? { tooltip } : {}),
        ...(crosshair ? { crosshair } : {})
      }

      const container = new XYContainer(wrapper, containerConfig, this.xyRecords)
      this.afterContainerCreated()
      return container
    },
    createPlotlineComponents() {
      if (!this.yMarkers?.length) return []
      return (this.yMarkers as Array<Marker>).map(
        (marker) =>
          new Plotline({
            axis: AxisType.Y,
            value: marker.value,
            labelText: marker.label,
            labelPosition: PlotlineLabelPosition.TopLeft,
            labelOffsetX: 0,
            labelOffsetY: 4,
            color: marker.options?.stroke,
            labelColor: marker.options?.labelColor,
            lineStyle: marker.options?.lineType === 'dashed' ? ([3, 4] as number[]) : undefined
          })
      )
    },
    createPlotbandComponents() {
      if (!this.yRegions?.length) return []
      return (this.yRegions as Array<Region>).map(
        (region) =>
          new Plotband({
            axis: AxisType.Y,
            from: region.start,
            to: region.end,
            labelText: region.label,
            color: region.options?.fill
          })
      )
    },
    createTooltip() {
      if (!process.client) return null
      return new Tooltip({
        className: 'z-chart-tooltip',
        container: document.body,
        showDelay: 0,
        hideDelay: 0
      })
    },
    createCrosshairTemplate(datum: XYRecord): string {
      return renderTooltipHtml({
        datum,
        dataSets: this.dataSets,
        palette: this.palette,
        xTickFormat: this.xTickFormat,
        tooltipOptions: this.tooltipOptions,
        humanizedTooltips: this.humanizedTooltips
      })
    },
    ensureLineAreaGradients(chartType: string) {
      const wrapper = this.wrapperEl
      if (!wrapper) return
      const svg = wrapper.querySelector('svg') as SVGSVGElement | null
      if (!svg) return

      let defs = svg.querySelector('defs') as SVGDefsElement | null
      if (!defs) {
        defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
        svg.insertBefore(defs, svg.firstChild)
      }

      const lineIndices = seriesByType(chartType, this.dataSets, 'line')
      lineIndices.forEach((index) => {
        const id = lineGradientId(index, this.chartId)
        const existing = defs?.querySelector(`#${id}`) as SVGLinearGradientElement | null
        const gradient =
          existing ??
          (document.createElementNS(
            'http://www.w3.org/2000/svg',
            'linearGradient'
          ) as SVGLinearGradientElement)

        gradient.setAttribute('id', id)
        gradient.setAttribute('x1', '0')
        gradient.setAttribute('y1', '0')
        gradient.setAttribute('x2', '0')
        gradient.setAttribute('y2', '1')

        while (gradient.firstChild) {
          gradient.removeChild(gradient.firstChild)
        }

        const color = this.palette[index] || this.palette[0]
        const stops = [
          { offset: '0%', opacity: '0.3' },
          { offset: '60%', opacity: '0.12' },
          { offset: '100%', opacity: '0' }
        ]

        stops.forEach((stopConfig) => {
          const stop = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
          stop.setAttribute('offset', stopConfig.offset)
          stop.setAttribute('stop-color', color)
          stop.setAttribute('stop-opacity', stopConfig.opacity)
          gradient.appendChild(stop)
        })

        if (!existing) {
          defs?.appendChild(gradient)
        }
      })
    },
    // Hook methods — override in child components
    buildChartComponents(_x: (d: XYRecord) => number): Array<unknown> {
      return []
    },
    createCrosshair(_tooltip?: Tooltip | null): Crosshair<XYRecord> | null {
      return null
    },
    afterChartUpdate() {
      // no-op by default; override in components that need post-render work
    },
    afterContainerCreated() {
      // no-op by default; override in components that need post-creation work
    }
  },
  mounted() {
    this.initChart()
  },
  beforeDestroy() {
    if (!process.client) return
    if (this.updateTimer) {
      clearTimeout(this.updateTimer)
      this.updateTimer = null
    }
    this.destroyChart()
  }
})
